import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Flame, CheckCircle2, Clock, Star, Sparkles, TrendingUp, Target } from 'lucide-react';

const MODE_LABELS = {
  hard_block: { label: 'Doctor-first', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  high_risk: { label: 'Extra care', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  conservative: { label: 'Gentle', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const StatCard = ({ icon: Icon, value, label, accent, testId }) => (
  <Card className="rounded-2xl" data-testid={testId}>
    <CardContent className="pt-4 pb-4 text-center">
      <div className={`h-9 w-9 rounded-full ${accent} flex items-center justify-center mx-auto mb-1.5`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-heading text-2xl font-semibold leading-none">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-1">{label}</p>
    </CardContent>
  </Card>
);

export const ProgressJourney = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/prenatal-coach/journey')
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="journey-loading">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }
  if (!data) return null;

  const weeks = [...(data.weeks || [])].reverse(); // newest first
  const totals = data.totals || {};
  const maxMinutes = Math.max(...(data.weeks || []).map((w) => w.minutes), 1);
  const hasHistory = (data.weeks || []).length > 0;

  return (
    <div className="space-y-4" data-testid="coach-journey">
      {/* Hero stats */}
      <div className="grid grid-cols-3 gap-2.5">
        <StatCard icon={Flame} value={data.streak_weeks || 0} accent="bg-orange-100 text-orange-600"
          label={data.streak_weeks === 1 ? 'week streak' : 'weeks streak'} testId="journey-streak" />
        <StatCard icon={CheckCircle2} value={totals.classes_completed || 0} accent="bg-emerald-100 text-emerald-600"
          label="classes done" testId="journey-classes" />
        <StatCard icon={Clock} value={totals.minutes || 0} accent="bg-primary/10 text-primary"
          label="mins practised" testId="journey-minutes" />
      </div>

      {/* Goals strip */}
      {(data.goals || []).length > 0 && (
        <Card className="rounded-2xl border-primary/20 bg-gradient-to-br from-primary/5 to-rose-50/60" data-testid="journey-goals-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium">Working towards</p>
              {data.goal_focus_completed > 0 && (
                <span className="text-[11px] text-muted-foreground ml-auto">
                  {data.goal_focus_completed} {data.goal_focus_completed === 1 ? 'class' : 'classes'} on goal focus
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {data.goals.map((g) => (
                <Badge key={g} className="bg-white/80 text-primary border-primary/20 text-[11px]">{g}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Practice growth */}
      {hasHistory && (
        <Card className="rounded-2xl" data-testid="journey-growth-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              <p className="text-sm font-medium">Practice growth</p>
              <span className="text-[11px] text-muted-foreground ml-auto">minutes per week</span>
            </div>
            <div className="flex items-end gap-1.5 h-20">
              {data.weeks.map((w) => (
                <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`Week ${w.pregnancy_week}: ${w.minutes} min`}>
                  <div className="w-full rounded-t-md bg-primary/15 relative overflow-hidden transition-all duration-500"
                    style={{ height: `${Math.max((w.minutes / maxMinutes) * 100, w.completed > 0 ? 8 : 3)}%` }}>
                    <div className="absolute bottom-0 w-full bg-primary/70 rounded-t-md"
                      style={{ height: w.completed > 0 ? '100%' : '0%' }} />
                  </div>
                  <span className="text-[9px] text-muted-foreground truncate">W{w.pregnancy_week}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Journey timeline */}
      <Card className="rounded-2xl" data-testid="journey-timeline">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium">Your journey, week by week</p>
          </div>
          {!hasHistory ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Your journey begins with your first completed class — it'll appear here.
            </p>
          ) : (
            <div className="relative pl-1">
              {weeks.map((w, i) => {
                const isCurrent = w.week_start === data.current_week_start;
                const done = w.completed > 0;
                const modeBadge = MODE_LABELS[w.mode];
                return (
                  <div key={w.week_start}
                    className="relative flex gap-3 pb-5 last:pb-0 animate-in fade-in slide-in-from-bottom-2 duration-300 fill-mode-both"
                    style={{ animationDelay: `${Math.min(i * 50, 300)}ms` }}
                    data-testid={`journey-week-${w.week_start}`}>
                    {/* connector line */}
                    {i < weeks.length - 1 && (
                      <div className={`absolute left-[15px] top-9 bottom-0 w-0.5 ${done ? 'bg-primary/30' : 'bg-border'}`} />
                    )}
                    {/* node */}
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 z-10 text-[11px] font-semibold border-2
                      ${isCurrent ? 'bg-primary text-primary-foreground border-primary shadow-md ring-4 ring-primary/15'
                        : done ? 'bg-primary/10 text-primary border-primary/30'
                        : 'bg-muted text-muted-foreground border-border'}`}>
                      {w.pregnancy_week}
                    </div>
                    {/* content */}
                    <div className={`flex-1 min-w-0 rounded-xl px-3 py-2.5 ${isCurrent ? 'bg-primary/5 border border-primary/15' : done ? 'bg-emerald-50/50' : 'bg-muted/30'}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-medium">Week {w.pregnancy_week}</p>
                        {isCurrent && <Badge className="bg-primary/10 text-primary border-primary/20 text-[9px]">This week</Badge>}
                        {modeBadge && <Badge className={`${modeBadge.cls} text-[9px]`}>{modeBadge.label}</Badge>}
                        {w.avg_rating && (
                          <span className="text-[11px] text-amber-600 flex items-center gap-0.5 ml-auto">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />{w.avg_rating}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {done
                          ? `${w.completed} of ${w.planned} classes • ${w.minutes} min${w.moderate > 0 ? ` • ${w.moderate} moderate` : ''}`
                          : isCurrent ? 'In progress — your practices are waiting' : 'A rest week — and that\u2019s okay'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
